import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { CurrencyInputField, SelectField, ToggleField } from '@brighte/form-components';
import {
  ApplicantType,
  HouseholdType,
  Period,
  householdIncomeCashSchema,
  householdIncomeLoanSchema,
} from '@brighte/validation';
import { StepNavigator } from '@components/StepNavigator';
import { useLaunchDarklyContextUpdate } from '@hooks';
import { Alert } from '@spark-web/alert';
import { Columns } from '@spark-web/columns';
import { Field } from '@spark-web/field';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import { useApplicationData } from '../../provider';
import {
  APPLICATION_STEPS,
  HOUSEHOLD,
  LABELS,
  PLACEHOLDER,
  PURCHASE_CATEGORY_SLUGS,
  TIME_FREQUENCY,
  getFormFields,
  isFieldExists,
} from '../../utils';
import ApplicationDetails from './ApplicantDetails';
import CreditCard from './CreditCard';
import HouseholdIncome from './HouseholdIncome';
import InvestmentProperties from './InvestmentProperties';
import Mortgage from './Mortgage';
import OtherLoans from './OtherLoans';
import PartnerIncome from './PartnerIncome';
import Rent from './Rent';
import { useHouseholdSummary } from './hooks';

function SectionHeader({ children }: { children: string }) {
  return (
    <Text as="h3" size="small" weight="bold" tone="primaryActive" transform="uppercase">
      {children}
    </Text>
  );
}

function Household() {
  const {
    TITLE,
    DESCRIPTION,
    ADD_PARTNER,
    MARITAL_STATUS_OPTIONS,
    DEPENDANTS_OPTIONS,
    HOUSING_COSTS_HEADER,
    INVESTMENT_PROPERTIES_HEADER,
    DEBT_HEADER,
    CREDIT_HEADER,
    LIVING_EXPENSES_HEADER,
    HOUSEHOLD_EXPENSES_LABEL,
    HOUSEHOLD_EXPENSES_SUBTITLE,
    HOUSEHOLD_ASSESSMENT_INFORMATION_MESSAGE,
  } = HOUSEHOLD;

  const { MARITAL_STATUS, DEPENDANTS, FREQUENCY, AMOUNT } = LABELS;

  const {
    HouseholdDetails: household,
    Finance: finance,
    quote,
  } = useApplicationData(['HouseholdDetails', 'Finance', 'quote']);
  const financialProductId = quote?.financialProduct?.id ?? '';
  const isCashApplication = finance?.payment?.isCashApplication;
  const householdIncomeSchema = isCashApplication ? householdIncomeCashSchema : householdIncomeLoanSchema;

  const solarCategorySlugs = [PURCHASE_CATEGORY_SLUGS.SOLAR_SYSTEM, PURCHASE_CATEGORY_SLUGS.SOLAR_BATTERY_SYSTEM];
  const allCategorySlugs = [quote?.category?.slug, ...(quote?.relatedReferrals?.map(rq => rq.category?.slug) ?? [])];
  const isSolarApplication = allCategorySlugs.some(slug => slug && solarCategorySlugs.includes(slug));

  const flags = useFlags();
  const vendorId = quote?.vendor?.publicId ?? '';
  const updateLDContext = useLaunchDarklyContextUpdate();

  useEffect(() => {
    if (financialProductId && vendorId) {
      updateLDContext({
        financialProduct: { key: financialProductId },
        vendorId: { key: vendorId },
      });
    }
  }, [financialProductId, vendorId]);

  const hideJointApplicationOption = flags['hide-joint-applicant-option'] ?? false;

  return (
    <StepNavigator<HouseholdType>
      schema={householdIncomeSchema}
      name={APPLICATION_STEPS.HOUSEHOLD}
      title={TITLE}
      defaultValues={household ?? {}}
      completedContent={useHouseholdSummary({ isCashApplication })}
      isLoading={false}
    >
      {(formControls: UseFormReturn<HouseholdType>) => {
        const { control, watch, setValue } = formControls;
        const maritalStatus = watch('applicant.maritalStatus');
        const isRelationshipSingle = maritalStatus === 'single';
        const additionalFields = getFormFields(householdIncomeSchema, financialProductId);
        const isJoint = watch('hasJointApplicant');

        useEffect(() => {
          setValue('applicant.type', ApplicantType.Primary);
          setValue('payment.isCashApplication', isCashApplication);
          setValue('financialProductId', financialProductId);
        }, [setValue]);

        if (isCashApplication) {
          return (
            <Stack gap="xxlarge">
              <HouseholdIncome {...formControls} />
            </Stack>
          );
        }

        return (
          <Stack gap="xxlarge">
            <Text>{DESCRIPTION}</Text>

            <>
              <SelectField
                name="applicant.maritalStatus"
                control={control}
                label={MARITAL_STATUS}
                options={MARITAL_STATUS_OPTIONS}
                placeholder={PLACEHOLDER}
              />
              {isFieldExists(additionalFields, 'applicant.dependents') ? (
                <SelectField
                  name="applicant.dependents"
                  control={control}
                  label={DEPENDANTS}
                  options={DEPENDANTS_OPTIONS}
                  placeholder={PLACEHOLDER}
                />
              ) : null}
              {maritalStatus ? (
                <>
                  {isFieldExists(additionalFields, 'applicant.income.householdIncome.amount') ? (
                    <HouseholdIncome {...formControls} />
                  ) : null}
                  {!isRelationshipSingle && !hideJointApplicationOption ? (
                    <>
                      {isFieldExists(additionalFields, 'hasJointApplicant') ? (
                        <ToggleField name="hasJointApplicant" label={ADD_PARTNER} control={control} layout="inline" />
                      ) : null}
                    </>
                  ) : null}

                  {!isRelationshipSingle ? (
                    <>
                      {isFieldExists(additionalFields, 'applicant.income.partnerIncome.amount') ? (
                        <PartnerIncome {...formControls} />
                      ) : null}
                    </>
                  ) : null}
                  {isJoint ? <ApplicationDetails control={control} /> : null}

                  {isFieldExists(additionalFields, 'applicant.expense.mortgage.amount') ? (
                    <Stack gap="large" paddingTop="small">
                      <SectionHeader>{HOUSING_COSTS_HEADER}</SectionHeader>
                      <Stack gap="xxlarge">
                        <Mortgage
                          watch={watch}
                          control={control}
                          setValue={setValue}
                          additionalFields={additionalFields}
                        />
                        {isFieldExists(additionalFields, 'applicant.expense.rent.amount') ? (
                          <Rent watch={watch} control={control} setValue={setValue} />
                        ) : null}
                      </Stack>
                    </Stack>
                  ) : null}

                  {isFieldExists(additionalFields, 'applicant.expense.investmentProperties.amount') ? (
                    <Stack gap="large" paddingTop="small">
                      <SectionHeader>{INVESTMENT_PROPERTIES_HEADER}</SectionHeader>
                      <InvestmentProperties
                        watch={watch}
                        control={control}
                        setValue={setValue}
                        additionalFields={additionalFields}
                      />
                    </Stack>
                  ) : null}

                  {isFieldExists(additionalFields, 'applicant.expense.otherLoans') ? (
                    <Stack gap="large" paddingTop="small">
                      <SectionHeader>{DEBT_HEADER}</SectionHeader>
                      <OtherLoans watch={watch} control={control} setValue={setValue} />
                    </Stack>
                  ) : null}

                  {isFieldExists(additionalFields, 'applicant.expense.creditCards.limit') ? (
                    <Stack gap="large" paddingTop="small">
                      <SectionHeader>{CREDIT_HEADER}</SectionHeader>
                      <CreditCard watch={watch} control={control} setValue={setValue} />
                    </Stack>
                  ) : null}

                  {isFieldExists(additionalFields, 'applicant.expense.household') ? (
                    <Stack gap="large" paddingTop="small">
                      <SectionHeader>{LIVING_EXPENSES_HEADER}</SectionHeader>
                      <Field label={HOUSEHOLD_EXPENSES_LABEL} description={HOUSEHOLD_EXPENSES_SUBTITLE}>
                        <Columns gap="small" collapseBelow="tablet">
                          <SelectField
                            name="applicant.expense.household.period"
                            control={control}
                            label={FREQUENCY}
                            fieldProps={{ labelVisibility: 'hidden' }}
                            options={TIME_FREQUENCY.filter(option => option.value !== Period.Annually)}
                            placeholder={PLACEHOLDER}
                          />
                          <CurrencyInputField
                            name="applicant.expense.household.amount"
                            control={control}
                            label={AMOUNT}
                            FieldProps={{ labelVisibility: 'hidden' }}
                          />
                        </Columns>
                      </Field>
                    </Stack>
                  ) : null}
                </>
              ) : null}
              {isSolarApplication && (
                <Alert tone="info">
                  <Text size="small">{HOUSEHOLD_ASSESSMENT_INFORMATION_MESSAGE}</Text>
                </Alert>
              )}
            </>
          </Stack>
        );
      }}
    </StepNavigator>
  );
}

export default Household;
